import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";

const T = {
  ar: {
    badge: "✨ الخدمة الأولى في المنطقة",
    title1: "مغاسل",
    title2: "القمر الأبيض",
    subtitle: "نستلم ملابسك من باب منزلك ونعيدها إليك نظيفة كالقمر",
    orderBtn: "🧺 اطلب الاستلام الآن",
    wheelBtn: "🎡 جرّب حظك",
    stat1: "1000+", stat1L: "عميل سعيد",
    stat2: "12", stat2L: "ساعة توصيل",
    stat3: "5⭐", stat3L: "تقييم العملاء",
  },
  en: {
    badge: "✨ #1 Service in the Region",
    title1: "White Moon",
    title2: "Laundry",
    subtitle: "We pick up your clothes from your doorstep and return them clean as the moon",
    orderBtn: "🧺 Book Pickup Now",
    wheelBtn: "🎡 Try Your Luck",
    stat1: "1000+", stat1L: "Happy Clients",
    stat2: "12", stat2L: "Hour Delivery",
    stat3: "5⭐", stat3L: "Customer Rating",
  }
};

export default function HeroSection() {
  const { lang } = useApp();
  const tr = T[lang] || T.ar;
  const isAr = lang === "ar";

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden" dir={isAr ? "rtl" : "ltr"}>
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1E5FA8]/90 via-[#1a4f8a]/80 to-[#0e3060]/95 z-0" />
      
      {/* Animated gradient boxes */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* Top-left floating box */}
        <div className="absolute -top-10 -left-10 w-64 h-64 bg-gradient-to-br from-white/15 to-transparent rounded-3xl blur-2xl animate-pulse" 
             style={{ animationDuration: '3s' }} />
        
        {/* Center-right glow box */}
        <div className="absolute top-1/4 -right-20 w-72 h-72 bg-gradient-to-bl from-accent/25 to-transparent rounded-full blur-3xl" 
             style={{ animation: 'pulse 4s ease-in-out infinite' }} />
        
        {/* Bottom-left accent box */}
        <div className="absolute -bottom-16 left-1/4 w-56 h-56 bg-gradient-to-tr from-[#1E5FA8]/30 to-transparent rounded-3xl blur-2xl" 
             style={{ animation: 'pulse 5s ease-in-out infinite' }} />
        
        {/* Small floating particles */}
        <div className="absolute top-20 left-1/3 w-24 h-24 bg-white/10 rounded-full blur-xl animate-bounce" 
             style={{ animationDuration: '3s' }} />
        <div className="absolute bottom-32 right-1/4 w-32 h-32 bg-accent/15 rounded-full blur-xl animate-bounce" 
             style={{ animationDuration: '4s', animationDelay: '1s' }} />
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" 
             style={{ 
               backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
               backgroundSize: '40px 40px' 
             }} />
      </div>

      {/* Floating bubbles */}
      {["💧","🫧","✨","🌙"].map((icon, i) => (
        <span key={i} className="absolute text-2xl opacity-20 animate-pulse pointer-events-none select-none"
          style={{ top: `${15 + i * 20}%`, left: isAr ? `${10 + i * 18}%` : `${75 - i * 18}%`, animationDelay: `${i * 0.5}s` }}>
          {icon}
        </span>
      ))}

      <div className="relative z-10 max-w-6xl mx-auto px-4 py-28 grid lg:grid-cols-2 gap-10 items-center w-full">
        {/* Text */}
        <div className="text-white space-y-6">
          <span className="inline-block bg-white/15 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-bold px-4 py-2 rounded-full">
            {tr.badge}
          </span>
          <h1 className="text-5xl lg:text-6xl font-black leading-tight">
            {tr.title1} <br />
            <span className="text-[#7ec8e3]">{tr.title2}</span>
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-md">{tr.subtitle}</p>

          <div className="flex flex-wrap gap-3 pt-2">
            <Link to="/laundry"
              className="flex items-center gap-2 bg-white text-[#1E5FA8] font-black px-6 py-3.5 rounded-2xl hover:bg-white/90 transition-all shadow-xl shadow-black/20 text-sm hover:scale-105">
              {tr.orderBtn}
            </Link>
            <Link to="/wheel"
              className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-bold px-6 py-3.5 rounded-2xl hover:bg-white/20 transition-all text-sm">
              {tr.wheelBtn}
            </Link>
          </div>

          {/* Stats */}
          <div className="flex gap-6 pt-4">
            {[
              { v: tr.stat1, l: tr.stat1L },
              { v: tr.stat2, l: tr.stat2L },
              { v: tr.stat3, l: tr.stat3L },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl font-black text-white">{s.v}</p>
                <p className="text-xs text-white/60 font-medium">{s.l}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Visual / Logo */}
        <div className="flex justify-center items-center">
          <div className="relative">
            <div className="w-64 h-64 lg:w-80 lg:h-80 rounded-full overflow-hidden border-4 border-white/30 shadow-2xl pulse-glow">
              <img
                src="https://images.unsplash.com/photo-1545173168-9f1947eebb7f?w=600&h=600&fit=crop&fm=webp&q=80"
                alt="مغاسل القمر الأبيض - خدمات غسيل وكوي في الخبر"
                className="w-full h-full object-cover"
                width="600"
                height="600"
                decoding="async"
                fetchPriority="high"
              />
            </div>
            {/* Logo overlay */}
            <div className="absolute bottom-2 end-2 w-14 h-14 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-white/50">
              <img
                src="https://media.base44.com/images/public/user_69729b1c2410c5c031f1d29a/291b90bb1_.png"
                alt="logo"
                width="40"
                height="40"
                decoding="async"
                className="w-10 h-10 object-contain"
              />
            </div>
            {/* Orbit badges */}
            {[
              { icon: "🧺", label: isAr ? "غسيل" : "Wash", deg: 0 },
              { icon: "👔", label: isAr ? "كوي" : "Iron", deg: 120 },
              { icon: "✨", label: isAr ? "تنظيف" : "Clean", deg: 240 },
            ].map((b, i) => {
              const rad = (b.deg - 90) * Math.PI / 180;
              const r = 150;
              const x = Math.cos(rad) * r;
              const y = Math.sin(rad) * r;
              return (
                <div key={i}
                  className="absolute bg-white dark:bg-gray-800 rounded-2xl px-3 py-2 shadow-lg flex items-center gap-1.5 text-xs font-bold text-[#1E5FA8] dark:text-blue-300"
                  style={{ left: `calc(50% + ${x}px - 36px)`, top: `calc(50% + ${y}px - 18px)` }}>
                  <span>{b.icon}</span>{b.label}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Wave bottom */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0,60 C480,0 960,0 1440,60 L1440,60 L0,60 Z" fill="hsl(var(--background))" />
        </svg>
      </div>
    </section>
  );
}